import { AxiosPromise, AxiosResponse } from 'axios';
import { apiService } from '@app/services';

const ignoredTypes: string[] = [
    'AgentProfileDecrypted',
    'ExternalService',
    'Job',
    'Tenant',
    'TenantSetting',
    'RootKey',
    'Database',
    'DatabasePool',
    'Server',
    'SqlServer',
    'Secret',
    'AsymmetricSecretKeyBase',
    'EccSecretKey',
    'RsaSecretKey',
    'AesSecretKey',
    'ProtectedIdentityAttribute',
    'Import',
    'SyncEntry',
    'DailyIntervalTrigger',
    'CalendarTrigger',
    'SimpleTrigger',
    'Calendar',
    'Trigger',
    'CronTrigger',
    'JobOptions',
    'TenantInformation',
    'PersonWrapper',
    'ProtectedConfigurationEntry',
    'KeyValuePair_2OfString_String'
];
class ApiExplorerService {

    getApiDocumentation(): AxiosPromise {
        return apiService.get('/api/directory/$metadata')
            .catch((error: AxiosResponse) => Promise.reject(error));
    }

    isArray(o: any) {
        return Object.prototype.toString.apply(o) === '[object Array]';
    }

    parseNode(xmlNode: any, result: any) {
        if (xmlNode.nodeName === '#text' && xmlNode.nodeValue.trim() === '') {
            return;
        }

        let jsonNode: any = {};
        const existing = result[xmlNode.nodeName];
        if (existing) {
            if (!this.isArray(existing)) {
                result[xmlNode.nodeName] = [existing, jsonNode];
            }
            else {
                result[xmlNode.nodeName].push(jsonNode);
            }
        }
        else {
            result[xmlNode.nodeName] = jsonNode;
        }

        if (xmlNode.attributes) {
            const length = xmlNode.attributes.length;
            for (let i = 0; i < length; i++) {
                let attribute = xmlNode.attributes[i];
                jsonNode[attribute.nodeName] = attribute.nodeValue;
            }
        }

        for (let i = 0; i < xmlNode.childNodes.length; i++) {
            this.parseNode(xmlNode.childNodes[i], jsonNode);
        }
    }

    asArray(val: any) {
        if (!val) {
            return [];
        }

        if (Array.isArray(val)) {
            return val;
        }

        return [val];
    }

    parseSchema(sc: any) {
        let schema: any = {};

        let parseMethods = function (a: any, delegateType: any) {
            let bindingParam: any = [];
            let params = apiExplorerService.asArray(a.Parameter);

            if (params && params.length) {
                params.forEach(function (p) {
                    if (p.Name === 'bindingParameter') {
                        bindingParam = p.Type;
                    }
                });
            }

            if (!bindingParam) {
                // TODO: Uncomment when we will set up dev variable
                // console.log('Unknown binding: ', a);
                return;
            }

            if (bindingParam.indexOf('Collection(') >= 0) {
                let type = bindingParam.replace('Collection(', '').replace(')', '');

                let collActions = schema[type][delegateType].collection || [];

                collActions.push({ name: a.Name, parameters: params, returnType: a.ReturnType });

                schema[type][delegateType].collection = collActions;
            }
            else {
                let entityActions = schema[bindingParam][delegateType].entity || [];
                entityActions.push({ name: a.Name, parameters: params, returnType: a.ReturnType });
                schema[bindingParam][delegateType].entity = entityActions;
            }
        };

        let parseTypes = function (s: any, types: any, type: any) {
            let entityTypes = apiExplorerService.asArray(types);

            if (entityTypes && entityTypes.length) {
                entityTypes.forEach(function (e) {
                    let fqn = s.Namespace + '.' + e.Name;

                    let schemaType = schema[fqn] || {
                        name: fqn
                    };

                    schemaType.path = schemaType.path || null;
                    schemaType.entityPath = schemaType.entityPath || null;
                    schemaType.type = type;
                    schemaType.namespace = schemaType.namespace || s.Namespace || null;
                    schemaType.abstract = e.Abstract || false;
                    schemaType.baseType = e.BaseType || null;

                    schemaType.properties = apiExplorerService.asArray(e.Property)
                        .concat(apiExplorerService.asArray(e.NavigationProperty))
                        .sort(function (a, b) {
                            return a.Name.localeCompare(b.Name);
                        });

                    if (e.Member) {
                        schemaType.members = apiExplorerService.asArray(e.Member);
                    }

                    schema[fqn] = schemaType;
                });
            }
        };

        if (sc !== undefined) {
            sc.forEach(function (s: any) {
                if (s.EntityContainer) {
                    s.EntityContainer.EntitySet.forEach(function (es: any) {
                        schema[es.EntityType] = {
                            path: '/api/directory/' + es.Name,
                            entityPath: '/api/directory/' + es.Name + '({id})',
                            name: es.Name,
                            namespace: s.EntityContainer.Name,
                            bindings: es.NavigationPropertyBinding,
                            actions: {},
                            functions: {}
                        };
                    });
                }

                if (s.Action) {
                    s.Action.forEach((a: any) => {
                        if (a) { parseMethods(a, 'actions'); }
                    });
                }

                if (s.Function) {
                    s.Function.forEach((a: any) => {
                        parseMethods(a, 'functions');
                    });
                }
            });
        }

        if (sc !== undefined) {
            sc.forEach(function (s: any) {
                parseTypes(s, s.EntityType, 'Entity');
                parseTypes(s, s.ComplexType, 'Complex');
                parseTypes(s, s.EnumType, 'Enum');
            });
        }

        return schema;
    }

    parseName(key: any) {
        const last = key.lastIndexOf('.');

        return key.substring(last + 1);
    }

    ignoreType(item: any) {
        return ignoredTypes.indexOf(apiExplorerService.parseName(item.key)) >= 0;
    }

    convertSchema(obj: any): any[] {
        let inverted = Object.keys(obj).map(function (key) {
            let arr = obj[key];

            arr.key = key;

            return arr;
        }).filter(function (item) {
            return !apiExplorerService.ignoreType(item);
        });

        let sortOrder = ['Entity', 'Complex', 'Enum'];

        inverted.sort(function (a, b) {
            if (a.path && b.path) {
                return a.path.localeCompare(b.path);
            }

            if (!a.namespace && !b.namespace) {
                return 0;
            }

            if (!!a.namespace && !b.namespace) {
                return -1;
            }

            if (!a.namespace && !!b.namespace) {
                return 1;
            }

            return sortOrder.indexOf(a.type) - sortOrder.indexOf(b.type) ||
                a.namespace.localeCompare(b.namespace);
        });

        return inverted;
    }
}

export const apiExplorerService = new ApiExplorerService();
